import { useCallback, useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import {
  ClipboardCheck,
  CheckCircle2,
  Eye,
  Paperclip,
  X,
  Search,
  Calendar,
  SlidersHorizontal,
  ChevronUp,
  ChevronDown,
  Download,
  FileText,
} from "lucide-react";
import "./TicketsTecnico.css";
import { filesCloud, timestamp } from "../../firebase/config";
import { useAuthContext } from "../../hooks/useAuthContext";
import { useCollection } from "../../hooks/useCollection";
import { useFirestore } from "../../hooks/useFirestore";
import { ResTecnicoAccess } from "../../components/RoleBasedAccess";

const TALOS_STATUSES = [
  { value: "aperto", label: "Aperto" },
  { value: "assegnato", label: "Assegnato" },
  { value: "eseguito", label: "Eseguito" },
  { value: "caricato", label: "Caricato" },
  { value: "fatturato", label: "Fatturato" },
];

const settoriLegacy = [
  { value: "Edile", label: "Settore Edile" },
  { value: "Cucine", label: "Settore Equipment Cucine" },
  { value: "Intesa", label: "Settore Banca Intesa" },
  { value: "Elettrico", label: "Settore Elettrico" },
  { value: "FTV", label: "Settore Fotovoltaico" },
  { value: "ROBY", label: "Personale Roby" },
  { value: "Amministrazione", label: "Amministrazione" },
  { value: "Cessati", label: "Ex Dipendenti" },
];

const getStatusLabel = (status) =>
  TALOS_STATUSES.find((item) => item.value === status)?.label || "Da definire";

const toValidDate = (value) => {
  if (value == null) return null;
  let date;
  if (typeof value.toDate === "function") {
    try {
      date = value.toDate();
    } catch {
      return null;
    }
  } else if (value instanceof Date) {
    date = value;
  } else {
    date = new Date(value);
  }
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  return date;
};

const safeFormat = (value, pattern, options) => {
  const d = toValidDate(value);
  if (!d) return "";
  try {
    return format(d, pattern, options);
  } catch {
    return "";
  }
};

const formatFileSize = (bytes) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 KB";
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }
  return `${Math.round(bytes / 1024)} KB`;
};

export default function TicketsTecnico() {
  const { user } = useAuthContext();
  const {
    documents: tickets,
    error: ticketsErr,
    loading: ticketsLoading,
  } = useCollection("tickets", null, ["createdAt", "desc"]);
  const { documents: settoriDocs } = useCollection("settori");
  const { documents: allUsers } = useCollection("users");
  const { documents: userProfile_doc } = useCollection("users", [
    ["id", "==", user?.uid || "__NO_UID__"],
  ]);
  const { updateDocument, resetResponse } = useFirestore("tickets");

  const [statusFilter, setStatusFilter] = useState("");
  const [monthFilter, setMonthFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "createdAt",
    direction: "desc",
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [editDraft, setEditDraft] = useState({
    status: "",
    dataIntervento: "",
    note: "",
    importo: "",
  });
  const [isBulkMarking, setIsBulkMarking] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState("");
  const [formError, setFormError] = useState("");

  const userProfile = useMemo(
    () => (Array.isArray(userProfile_doc) ? userProfile_doc[0] : null),
    [userProfile_doc],
  );

  const mySector = useMemo(() => {
    let sector = userProfile?.anagrafica?.settore;
    if (!sector && Array.isArray(allUsers)) {
      const me = allUsers.find((u) => u.id === user?.uid);
      sector = me?.anagrafica?.settore;
    }
    if (typeof sector === "string") {
      sector = sector.trim();
    }
    return sector;
  }, [userProfile, allUsers, user?.uid]);

  const userSectorLegacyLabel = useMemo(() => {
    if (!mySector) return "";
    const legacyObj = settoriLegacy.find((s) => s.value === mySector);
    return legacyObj ? legacyObj.label : mySector;
  }, [mySector]);

  const firestoreSectorId = useMemo(() => {
    if (!mySector || !Array.isArray(settoriDocs)) return null;
    const firestoreSector = settoriDocs.find(
      (s) => s.nome === userSectorLegacyLabel || s.nome === mySector,
    );
    return firestoreSector ? firestoreSector.id : null;
  }, [mySector, userSectorLegacyLabel, settoriDocs]);

  const isSuperSector = mySector === "ROBY";

  const monthOptions = useMemo(() => {
    if (!Array.isArray(tickets)) return [];
    const monthsSet = new Set();
    tickets.forEach((ticket) => {
      const key = safeFormat(ticket.createdAt, "yyyy-MM");
      if (key) monthsSet.add(key);
    });
    return Array.from(monthsSet).sort().reverse();
  }, [tickets]);

  const visibleTickets = useMemo(() => {
    if (!Array.isArray(tickets)) return [];

    const query = searchTerm.trim().toLowerCase();

    const matchSector = (ticket) => {
      if (isSuperSector) return true;
      if (!mySector) return false;

      if (firestoreSectorId && ticket.settore?.id === firestoreSectorId)
        return true;
      if (ticket.settore?.nome === userSectorLegacyLabel) return true;
      if (ticket.settore?.nome === mySector) return true;
      if (ticket.settore?.id === mySector) return true;
      if (ticket.settoreId === firestoreSectorId) return true;
      if (ticket.settoreId === mySector) return true;
      if (ticket.settoreNome === userSectorLegacyLabel) return true;
      if (ticket.settoreNome === mySector) return true;

      return false;
    };

    const filtered = tickets.filter((ticket) => {
      if (ticket.tipo !== "talos") return false;

      if (!matchSector(ticket)) return false;

      if (statusFilter && ticket.status !== statusFilter) return false;

      const dateForMonthFilterVal = ticket.dataTicket ?? ticket.createdAt;
      const monthKey = safeFormat(dateForMonthFilterVal, "yyyy-MM");
      if (monthFilter && monthKey !== monthFilter) return false;

      if (query) {
        const dataInterventoFormatted = safeFormat(
          ticket.dataIntervento,
          "d MMM yyyy",
          { locale: it },
        );
        const dataTicketFormatted = safeFormat(
          dateForMonthFilterVal,
          "d MMM yyyy",
          { locale: it },
        );
        const haystack = [
          ticket.nrTicket,
          ticket.ordine,
          ticket.importo != null ? String(ticket.importo) : "",
          ticket.descrizione,
          ticket.luogo,
          ticket.settore?.nome,
          getStatusLabel(ticket.status),
          ticket.note,
          dataInterventoFormatted,
          dataTicketFormatted,
        ]
          .filter(Boolean)
          .map((v) => String(v).toLowerCase())
          .join(" ");
        if (!haystack.includes(query)) return false;
      }

      return true;
    });

    return [...filtered].sort((left, right) => {
      const getValue = (ticket) => {
        switch (sortConfig.key) {
          case "nrTicket":
            return ticket.nrTicket || "";
          case "status":
            return getStatusLabel(ticket.status);
          case "ordine":
            return ticket.ordine || "";
          case "importo": {
            const n =
              typeof ticket.importo === "number"
                ? ticket.importo
                : Number.parseFloat(String(ticket.importo || 0));
            return Number.isFinite(n) ? n : 0;
          }
          case "dataIntervento": {
            const d = toValidDate(ticket.dataIntervento);
            return d ? d.getTime() : 0;
          }
          case "luogo":
            return ticket.luogo || "";
          case "note":
            return ticket.note || "";
          default: {
            const d = toValidDate(ticket.createdAt);
            return d ? d.getTime() : 0;
          }
        }
      };

      const leftValue = getValue(left);
      const rightValue = getValue(right);

      if (typeof leftValue === "number" && typeof rightValue === "number") {
        return sortConfig.direction === "asc"
          ? leftValue - rightValue
          : rightValue - leftValue;
      }

      const comparison = String(leftValue).localeCompare(
        String(rightValue),
        "it",
        { numeric: true, sensitivity: "base" },
      );

      return sortConfig.direction === "asc" ? comparison : -comparison;
    });
  }, [
    tickets,
    isSuperSector,
    mySector,
    firestoreSectorId,
    userSectorLegacyLabel,
    statusFilter,
    monthFilter,
    searchTerm,
    sortConfig,
  ]);

  const handleSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction:
        current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleClearFilters = () => {
    setStatusFilter("");
    setMonthFilter("");
    setSearchTerm("");
  };

  const handleSegnaTuttiComeVisti = async () => {
    if (!visibleTickets.length || isBulkMarking) return;
    resetResponse();
    setIsBulkMarking(true);
    setSaveSuccess("");
    setFormError("");

    const now = timestamp.fromDate(new Date());
    const updatePromises = visibleTickets.map((ticket) => {
      const alreadySeen = Array.isArray(ticket.vistiRT)
        ? ticket.vistiRT.some((v) => v.userId === user?.uid)
        : false;
      if (alreadySeen) return Promise.resolve({ status: "skipped" });
      return updateDocument(ticket.id, {
        vistiRT: [
          ...(ticket.vistiRT || []),
          { userId: user?.uid, timestamp: now },
        ],
        updatedAt: now,
      });
    });

    const results = await Promise.allSettled(updatePromises);
    const successCount = results.filter(
      (r) => r.status === "fulfilled" && r.value?.status !== "skipped",
    ).length;
    const skippedCount = results.filter(
      (r) => r.status === "fulfilled" && r.value?.status === "skipped",
    ).length;
    const failCount = results.filter((r) => r.status === "rejected").length;

    setSaveSuccess(
      `Operazione completata: ${successCount} aggiornati, ${skippedCount} già presenti, ${failCount} errori.`,
    );
    setIsBulkMarking(false);
  };

  const handleOpenTicket = async (ticket) => {
    if (!ticket?.id) return;
    resetResponse();
    setSelectedTicket(ticket);
    const parsedImporto =
      typeof ticket.importo === "number"
        ? ticket.importo
        : Number.parseFloat(String(ticket.importo || 0));
    const importoString = Number.isFinite(parsedImporto)
      ? parsedImporto.toFixed(2).replace(".", ",")
      : "";
    setEditDraft({
      status: ticket.status || "aperto",
      dataIntervento: safeFormat(ticket.dataIntervento, "yyyy-MM-dd"),
      note: ticket.note || "",
      importo: importoString,
    });
    setIsModalOpen(true);
    setFormError("");
    setSaveSuccess("");

    const alreadySeen = Array.isArray(ticket.vistiRT)
      ? ticket.vistiRT.some((v) => v.userId === user?.uid)
      : false;
    if (!alreadySeen) {
      try {
        const now = timestamp.fromDate(new Date());
        await updateDocument(ticket.id, {
          vistiRT: [
            ...(ticket.vistiRT || []),
            { userId: user?.uid, timestamp: now },
          ],
        });
      } catch (err) {
        // non bloccare l'apertura
      }
    }
  };

  const handleDraftChange = (field, value) => {
    setEditDraft((current) => ({ ...current, [field]: value }));
  };

  const handleSubmitPatch = async (e) => {
    e.preventDefault();
    if (!selectedTicket?.id) return;

    resetResponse();
    setFormError("");
    setSaveSuccess("");
    setIsSubmitting(true);

    if (!editDraft.status) {
      setFormError("Seleziona uno status valido.");
      setIsSubmitting(false);
      return;
    }
    if (editDraft.status === "fatturato") {
      setFormError(
        "Lo status 'Fatturato' puo essere impostato solo dall'amministrazione.",
      );
      setIsSubmitting(false);
      return;
    }

    const parseImporto = (raw) => {
      if (raw == null) return null;
      if (typeof raw === "number") {
        return Number.isFinite(raw) ? raw : null;
      }
      const cleaned = String(raw)
        .replace(/€/g, "")
        .replace(/\s/g, "")
        .replace(",", ".")
        .trim();
      if (cleaned === "" || cleaned === "-") return null;
      const n = Number.parseFloat(cleaned);
      return Number.isFinite(n) ? n : null;
    };

    const importoParsed = parseImporto(editDraft.importo);

    const now = timestamp.fromDate(new Date());
    const patch = {
      status: editDraft.status,
      statusLabel: getStatusLabel(editDraft.status),
      note: editDraft.note.trim(),
      dataIntervento: editDraft.dataIntervento
        ? timestamp.fromDate(new Date(editDraft.dataIntervento))
        : null,
      importo: importoParsed,
      updatedAt: now,
    };

    const alreadySeen = Array.isArray(selectedTicket.vistiRT)
      ? selectedTicket.vistiRT.some((v) => v.userId === user?.uid)
      : false;
    if (!alreadySeen) {
      patch.vistiRT = [
        ...(selectedTicket.vistiRT || []),
        { userId: user?.uid, timestamp: now },
      ];
    }

    try {
      await updateDocument(selectedTicket.id, patch);
      setIsModalOpen(false);
      setSaveSuccess("Ticket aggiornato correttamente");
    } catch (err) {
      setFormError("Errore durante il salvataggio delle modifiche.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAttachmentDownload = async (attachment) => {
    if (!attachment) return;
    const url = attachment.downloadUrl || attachment.url;
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }
    try {
      const path = attachment.fullPath || attachment.storagePath;
      if (!path) return;
      const ref = filesCloud.ref(path);
      const resolved = await ref.getDownloadURL();
      window.open(resolved, "_blank", "noopener,noreferrer");
    } catch (err) {
      setFormError("Impossibile recuperare l'allegato.");
    }
  };

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedTicket(null);
    setEditDraft({ status: "", dataIntervento: "", note: "", importo: "" });
    setFormError("");
  }, []);

  useEffect(() => {
    if (!isModalOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [closeModal, isModalOpen]);

  const attachments = useMemo(
    () =>
      Array.isArray(selectedTicket?.attachments)
        ? selectedTicket.attachments
        : [],
    [selectedTicket],
  );

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) return null;
    return sortConfig.direction === "asc" ? (
      <ChevronUp size={14} style={{ marginLeft: 4, verticalAlign: "middle" }} />
    ) : (
      <ChevronDown
        size={14}
        style={{ marginLeft: 4, verticalAlign: "middle" }}
      />
    );
  };

  return (
    <ResTecnicoAccess>
      <div className="tickets-page">
        <div className="tickets-header">
          <div>
            <div className="tickets-kicker">Tickets Talos</div>
            <h1>Gestione Tickets</h1>
            <p>
              Visualizza i ticket Talos assegnati al tuo settore, aggiorna
              status, data intervento e note condivise.
            </p>
          </div>
          <div className="tickets-header-actions">
            <div className="tickets-header-badge">
              {visibleTickets?.length ?? 0} ticket nel tuo settore
            </div>
            <button
              type="button"
              className="tickets-primary-btn"
              onClick={handleSegnaTuttiComeVisti}
              disabled={!visibleTickets?.length || isBulkMarking}
            >
              {isBulkMarking ? (
                <>
                  <ClipboardCheck size={17} />
                  Aggiornamento...
                </>
              ) : (
                <>
                  <CheckCircle2 size={17} />
                  Segna tutti come visti
                </>
              )}
            </button>
          </div>
        </div>

        {!mySector && (
          <div
            className="tickets-empty error"
            style={{ marginBottom: 16, marginTop: 0 }}
          >
            Attenzione: il tuo profilo non ha un settore assegnato. Contatta
            l'amministrazione.
          </div>
        )}

        <section className="tickets-panel">
          <div className="tickets-panel-head">
            <div className="tickets-panel-title">
              <SlidersHorizontal size={18} />
              <div>
                <h2>Archivio tickets</h2>
                <p>
                  Filtra per status, periodo o testo libero, quindi ordina le
                  colonne.
                </p>
              </div>
            </div>
          </div>

          {saveSuccess && (
            <div className="tickets-inline-message success tickets-list-feedback">
              {saveSuccess}
            </div>
          )}

          {ticketsErr && (
            <div className="tickets-empty error">
              Errore nel caricamento dei tickets.
            </div>
          )}

          <div className="tickets-filters">
            <div className="tickets-filter-row">
              <div className="tickets-filter-field tickets-filter-search">
                <label>
                  <Search size={14} style={{ marginRight: 6 }} />
                  Cerca
                </label>
                <input
                  type="text"
                  placeholder="Numero ticket, ordine, descrizione, status, luogo..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="tickets-filter-input"
                />
              </div>

              <div className="tickets-filter-field">
                <label>Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="tickets-filter-input"
                >
                  <option value="">Tutti gli status</option>
                  {TALOS_STATUSES.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="tickets-filter-field">
                <label>
                  <Calendar size={14} style={{ marginRight: 6 }} />
                  Periodo mese
                </label>
                <div className="tickets-filter-period">
                  <select
                    value={monthFilter}
                    onChange={(e) => setMonthFilter(e.target.value)}
                    className="tickets-filter-input"
                  >
                    <option value="">Tutti i mesi</option>
                    {monthOptions.map((key) => {
                      const [y, m] = key.split("-");
                      const label = format(
                        new Date(Number(y), Number(m) - 1, 1),
                        "MMMM yyyy",
                        { locale: it },
                      );
                      return (
                        <option key={key} value={key}>
                          {label.charAt(0).toUpperCase() + label.slice(1)}
                        </option>
                      );
                    })}
                  </select>
                  <button
                    type="button"
                    className="tickets-clear-btn"
                    onClick={handleClearFilters}
                    disabled={!searchTerm && !statusFilter && !monthFilter}
                    title="Pulisci filtri"
                  >
                    ×
                  </button>
                </div>
              </div>
            </div>
          </div>

          {!ticketsErr &&
            (!tickets || tickets.length === 0) &&
            !ticketsLoading && (
              <div className="tickets-empty">
                Nessun ticket registrato al momento.
              </div>
            )}

          {!ticketsErr && Array.isArray(tickets) && tickets.length > 0 && (
            <div className="tickets-table-wrapper">
              {visibleTickets.length === 0 ? (
                <div className="tickets-empty">
                  Nessun ticket corrisponde ai criteri di ricerca.
                </div>
              ) : (
                <table className="tickets-table">
                  <thead>
                    <tr>
                      <th
                        className="sortable"
                        onClick={() => handleSort("nrTicket")}
                      >
                        Nr. ticket
                        <SortIcon column="nrTicket" />
                      </th>
                      <th
                        className="sortable"
                        onClick={() => handleSort("status")}
                      >
                        Status
                        <SortIcon column="status" />
                      </th>
                      <th
                        className="sortable"
                        onClick={() => handleSort("ordine")}
                      >
                        Ordine
                        <SortIcon column="ordine" />
                      </th>
                      <th
                        className="sortable"
                        onClick={() => handleSort("importo")}
                      >
                        Importo
                        <SortIcon column="importo" />
                      </th>
                      <th
                        className="sortable"
                        onClick={() => handleSort("dataIntervento")}
                      >
                        Data intervento
                        <SortIcon column="dataIntervento" />
                      </th>
                      <th
                        className="sortable"
                        onClick={() => handleSort("luogo")}
                      >
                        Luogo
                        <SortIcon column="luogo" />
                      </th>
                      <th
                        className="sortable"
                        style={{ width: 320, minWidth: 280, maxWidth: 360 }}
                        onClick={() => handleSort("note")}
                      >
                        Note
                        <SortIcon column="note" />
                      </th>
                      <th style={{ textAlign: "right" }}>Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleTickets.map((ticket) => (
                      <tr key={ticket.id}>
                        <td className="tickets-cell-strong">
                          {ticket.nrTicket || "--"}
                        </td>
                        <td>
                          <span className="tickets-chip status">
                            {getStatusLabel(ticket.status)}
                          </span>
                        </td>
                        <td className="tickets-cell-strong">
                          {ticket.ordine || "--"}
                        </td>
                        <td>
                          {(() => {
                            const parsed =
                              typeof ticket.importo === "number"
                                ? ticket.importo
                                : Number.parseFloat(
                                    String(ticket.importo || 0),
                                  );
                            const n = Number.isFinite(parsed) ? parsed : null;
                            return n != null ? (
                              `€ ${n.toLocaleString("it-IT", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}`
                            ) : (
                              <span style={{ color: "#94a3b8" }}>--</span>
                            );
                          })()}
                        </td>
                        <td>
                          {(() => {
                            const formatted = safeFormat(
                              ticket.dataIntervento,
                              "d MMM yyyy",
                              { locale: it },
                            );
                            return formatted ? (
                              formatted
                            ) : (
                              <span style={{ color: "#94a3b8" }}>--</span>
                            );
                          })()}
                        </td>
                        <td>{ticket.luogo || "-"}</td>
                        <td
                          style={{
                            width: 320,
                            minWidth: 280,
                            maxWidth: 360,
                          }}
                        >
                          <span
                            style={{
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              lineHeight: "1.35",
                              fontSize: 13,
                              color: "#475569",
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                            }}
                            title={ticket.note || ""}
                          >
                            {ticket.note && String(ticket.note).trim()
                              ? ticket.note
                              : "--"}
                          </span>
                        </td>
                        <td>
                          <div className="tickets-table-actions">
                            <button
                              type="button"
                              className="tickets-secondary-btn tickets-table-btn"
                              onClick={() => handleOpenTicket(ticket)}
                            >
                              <Eye size={15} />
                              Gestisci
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </section>

        {isModalOpen && selectedTicket && (
          <div
            className="tickets-modal-backdrop"
            onClick={closeModal}
            role="presentation"
          >
            <section
              className="tickets-modal"
              id="tickets-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="tickets-tecnico-title"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="tickets-modal-head">
                <div className="tickets-panel-title">
                  <ClipboardCheck size={18} />
                  <div>
                    <h2 id="tickets-tecnico-title">
                      Gestione Ticket #{selectedTicket?.nrTicket || "--"}
                    </h2>
                    <p>
                      Consulta i dati, aggiorna status, data intervento e note
                      condivise con l'amministrazione.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  className="tickets-modal-close"
                  onClick={closeModal}
                  aria-label="Chiudi modal"
                >
                  <X size={18} />
                </button>
              </div>

              <form className="tickets-modal-form" onSubmit={handleSubmitPatch}>
                <div className="tickets-modal-grid">
                  <section className="tickets-modal-section">
                    <div className="tickets-section-header">
                      <h3>Dati generali</h3>
                      <p>Informazioni di sola lettura del ticket.</p>
                    </div>
                    <div className="tickets-form">
                      <label className="tickets-field">
                        <span>Cliente</span>
                        <input type="text" value="MD Spa" disabled readOnly />
                      </label>
                      <label className="tickets-field">
                        <span>Settore</span>
                        <input
                          type="text"
                          value={selectedTicket.settore?.nome || "-"}
                          disabled
                          readOnly
                        />
                      </label>
                      <label className="tickets-field">
                        <span>Data ticket (Data richiesta)</span>
                        <input
                          type="text"
                          value={(() => {
                            const formatted = safeFormat(
                              selectedTicket.dataTicket ??
                                selectedTicket.createdAt,
                              "d MMM yyyy",
                              { locale: it },
                            );
                            return formatted || "-";
                          })()}
                          disabled
                          readOnly
                        />
                      </label>
                      <label className="tickets-field">
                        <span>Luogo</span>
                        <input
                          type="text"
                          value={selectedTicket.luogo || "-"}
                          disabled
                          readOnly
                        />
                      </label>
                      <label className="tickets-field">
                        <span>Ordine</span>
                        <input
                          type="text"
                          value={selectedTicket.ordine || "(non assegnato)"}
                          disabled
                          readOnly
                        />
                      </label>
                      <label className="tickets-field tickets-field-full">
                        <span>Descrizione</span>
                        <textarea
                          rows="4"
                          value={selectedTicket.descrizione || ""}
                          disabled
                          readOnly
                        />
                      </label>
                    </div>
                  </section>

                  <section className="tickets-modal-section">
                    <div className="tickets-section-header">
                      <h3>Aggiornamenti</h3>
                      <p>
                        Campi modificabili dal responsabile tecnico. Lo status
                        "Fatturato" e riservato agli admin.
                      </p>
                    </div>
                    <div className="tickets-form">
                      <label className="tickets-field">
                        <span>Status</span>
                        <select
                          value={editDraft.status}
                          onChange={(e) =>
                            handleDraftChange("status", e.target.value)
                          }
                        >
                          {TALOS_STATUSES.map((opt) => (
                            <option
                              key={opt.value}
                              value={opt.value}
                              disabled={opt.value === "fatturato"}
                            >
                              {opt.label}
                              {opt.value === "fatturato" ? " (solo admin)" : ""}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="tickets-field">
                        <span>Data intervento (opzionale)</span>
                        <input
                          type="date"
                          value={editDraft.dataIntervento}
                          onChange={(e) =>
                            handleDraftChange("dataIntervento", e.target.value)
                          }
                        />
                      </label>
                      <label className="tickets-field">
                        <span>Importo (€)</span>
                        <input
                          type="text"
                          value={editDraft.importo}
                          onChange={(e) =>
                            handleDraftChange("importo", e.target.value)
                          }
                          placeholder="es. 1250,50"
                        />
                      </label>
                      <label className="tickets-field tickets-field-full">
                        <span>Note (condivise con admin)</span>
                        <textarea
                          rows="5"
                          value={editDraft.note}
                          onChange={(e) =>
                            handleDraftChange("note", e.target.value)
                          }
                          placeholder="Inserisci aggiornamenti, dettagli sull'intervento o informazioni utili per l'amministrazione"
                        />
                      </label>
                    </div>
                  </section>

                  <section className="tickets-modal-section tickets-modal-section-full">
                    <div className="tickets-section-header">
                      <h3>
                        <FileText
                          size={16}
                          style={{
                            verticalAlign: "middle",
                            marginRight: 6,
                          }}
                        />
                        Allegati
                      </h3>
                      <p>
                        Documenti e immagini associati al ticket (sola lettura).
                      </p>
                    </div>

                    {attachments.length === 0 ? (
                      <div className="tickets-empty">
                        Nessun allegato disponibile per questo ticket.
                      </div>
                    ) : (
                      <div className="tickets-attachments-grid">
                        {attachments.map((attachment, idx) => {
                          const name =
                            attachment.originalName ||
                            attachment.name ||
                            `allegato-${idx + 1}`;
                          const size = formatFileSize(
                            attachment.size || attachment.originalSize || 0,
                          );
                          return (
                            <div
                              key={
                                attachment.storagePath ||
                                attachment.fullPath ||
                                `att-${idx}`
                              }
                              className="tickets-attachment-card"
                            >
                              <div className="tickets-attachment-main">
                                <Paperclip size={16} />
                                <div>
                                  <button
                                    type="button"
                                    className="tickets-link-btn"
                                    onClick={() =>
                                      handleAttachmentDownload(attachment)
                                    }
                                    style={{
                                      padding: 0,
                                      border: "none",
                                      background: "transparent",
                                      fontWeight: 700,
                                      color: "#0f172a",
                                    }}
                                  >
                                    {name}
                                  </button>
                                  <span>{size}</span>
                                </div>
                              </div>
                              <button
                                type="button"
                                className="tickets-link-btn"
                                onClick={() =>
                                  handleAttachmentDownload(attachment)
                                }
                              >
                                <Download size={14} />
                                Apri
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </section>
                </div>

                <div className="tickets-form-actions">
                  {formError && (
                    <div className="tickets-inline-message error">
                      {formError}
                    </div>
                  )}
                  <div className="tickets-form-buttons">
                    <button
                      type="button"
                      className="tickets-secondary-btn"
                      onClick={closeModal}
                      disabled={isSubmitting}
                    >
                      Annulla
                    </button>
                    <button
                      type="submit"
                      className="tickets-primary-btn"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Salvataggio..." : "Salva modifiche"}
                    </button>
                  </div>
                </div>
              </form>
            </section>
          </div>
        )}
      </div>
    </ResTecnicoAccess>
  );
}
